import globalNotification from 'live/services/global-notification'

import i18n from 'live/services/i18n'
import React from 'react'
import { ParamTypes, LastSavingDataType, ConfigResponseTypes, OriginalParamDataType } from './Parameters/kpiTypes'
import {
    DictionaryResponseTypes,
    DictionaryEntry
} from './Parameters/KPIGoalsConfiguration/acronymDictionary/dictionaryTypes'
import {
    CMEntry,
    CodeManagementResponseTypes
} from './Parameters/KPIGoalsConfiguration/codeManagement/codeManagementTypes'

/**
 * this func is a fetch func with all the response treatments that are shares across all the params page
 * it should receive a string with the type of info to fetch and it already handles the error message with live's global
 * notification
 *
 * if the response is empty it returns a default obj so the app doesnt break
 *
 */
export function fetchKpiData(
    urlParam: ParamTypes
): Promise<Response | Record<string, unknown>> | OriginalParamDataType {
    return fetch(`/services/plugin-kpis-drilling/config/${urlParam || ''}`)
        .then(r => r?.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            const result = res.body
            if (!result || !result.lastUpdated) {
                globalNotification.error(
                    <span>{`${i18n(`Something went wrong`)}: ${result?.message} - ${res.status}`}</span>,
                    10
                )

                return { data: [], lastUpdated: null }
            } else {
                return result
            }
        })
}

export function fetchCalculatedPersitedsChannels(): Promise<
    {
        id: string
        calculatedChannel: string
        relatedAssetsByType: Record<string, string[]>
        relatedChannels: Record<string, string>[]
    }[]
> {
    return fetch(`/services/plugin-calculated-to-persisted/ `)
        .then(r => {
            if (!r.ok) {
                return new Response(([] as unknown) as BodyInit, { status: r.status })
            }

            return r.json().then(data => ({ status: r.status, body: data }))
        })
        .then(res => {
            const result = res.body
            if (!result) {
                return []
            } else {
                return result
            }
        })
}

export function fetchKpiDataForAsset(urlParam: ParamTypes, id: string): Promise<Response | Record<string, unknown>> {
    return fetch(`/services/plugin-kpis-drilling/config/${urlParam || ''}${id ? '/' + id : ''}`)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            const result = res.body
            if (!result || !result.lastUpdated) {
                return { data: [], lastUpdated: null }
            } else {
                return result
            }
        })
}

export function fetchKpiDataForAssets(
    urlParam: ParamTypes,
    ids: string[]
): Promise<Response | Record<string, unknown>> {
    return fetch(`/services/plugin-kpis-drilling/config/${urlParam || ''}/get/goals`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ids: ids
        })
    })
        .then(r => r.json().then(data => ({ status: r.status, body: data[0] })))
        .then(res => {
            const result = res.body
            if (!result || !result.lastUpdated) {
                return { data: [], lastUpdated: null }
            } else {
                return result
            }
        })
}
/**
 *
 */
export function fetchConfigOptions(urlParam: ParamTypes): Promise<Response | Record<string, unknown>> {
    return fetch(`/services/plugin-kpis-drilling/config/${urlParam || ''}/configOptions`)
        .then(r => {
            return r.json().then(data => {
                return { status: r.status, body: data }
            })
        })
        .then(res => {
            const result = res.body
            if (!result) {
                globalNotification.error(
                    <span>{`${i18n(`Something went wrong`)}: ${result.body.message} - ${res.status}`}</span>,
                    10
                )

                return null
            } else {
                return result
            }
        })
}

/**
 * this func is a post func with all the response treatments that are shares across all the params page
 * it should receive a string with the type of info to fetch and it already handles the error and sucess message with live's global
 * notification
 *
 */
export function postKpiData(
    urlParam: string,
    obj: object,
    isTotalStandTimePost?: boolean
): Promise<void | { status: number; body: LastSavingDataType }> {
    return fetch(`/services/plugin-kpis-drilling/config/${urlParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(obj)
    })
        .then(r => r.json().then((data: LastSavingDataType) => ({ status: r.status, body: data })))
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <span>
                        {`${i18n(`For some reason, your request couldn't be saved`)}: ${res.body.message} - (${
                            res.status
                        }) `}
                    </span>,
                    10
                )

                return res
            } else {
                if (!isTotalStandTimePost) {
                    globalNotification.success(<span>{i18n(`Changes applied with success`)}</span>, 3)
                }

                return res
            }
        })
        .catch(err => console.error('there was an error in the post func', err))
}

export function postInterpolationParametersData(
    urlParam: string,
    obj: object
): Promise<void | { status: number; body: LastSavingDataType }> {
    return fetch(
        `/services/plugin-kpis-drilling/kpi/interpolation-parameters/save-interpolation-parameters/${urlParam}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(obj)
        }
    )
        .then(r => {
            return { status: r.status, body: undefined }
        })
        .then(res => {
            if (res.status !== 200) {
                globalNotification.error(
                    <span>
                        {`${i18n(`For some reason, your request couldn't be saved`)}: ${res.body.message} - (${
                            res.status
                        }) `}
                    </span>,
                    10
                )

                return res
            } else {
                globalNotification.success(<span>{i18n(`Changes applied with success`)}</span>, 3)

                return res
            }
        })
        .catch(err => console.error('there was an error in the post func', err))
}

/**
 *
 * The KpiDataElement CRUD are only useful for array-type ParamDataTypes.
 * They operate on a single element of the list, without changing the entire list.
 * Currently they are used only for goals.
 *
 */

export function postKpiDataElement(
    newEl: object,
    configType: ParamTypes,
    assetId = 'GLOBAL',
    silent = false
): Promise<ConfigResponseTypes | void> {
    return fetch(`/services/plugin-kpis-drilling/config/${configType}/${assetId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEl)
    })
        .then(res => (res?.ok ? res : Promise.reject(res))) // "ok" will check for status code from 200 to 299
        .then(r => r.json())
        .then(res => {
            if (!silent) {
                globalNotification.success(i18n(`The goal was created successfully`))
            }

            return res
        })
        .catch(err => {
            if (!err?.status) {
                // log error message to debug
                console.error('An internal error occurred while creating the goal:\n', err)
                globalNotification.error('An internal error occurred while creating the goal')

                return
            }
            err?.json().then(body =>
                globalNotification.error(
                    `${i18n(`For some reason the goal could not be created`)}: ${body?.message ?? err?.statusText} - (${
                        err?.status
                    })`
                )
            )
        })
}

export function updateKpiDataElement(
    updatedEl: object,
    elId: string,
    configType: ParamTypes,
    assetId = 'GLOBAL',
    silent = false
): Promise<ConfigResponseTypes | void> {
    return fetch(`/services/plugin-kpis-drilling/config/${configType}/${assetId}/${elId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEl)
    })
        .then(res => (res?.ok ? res : Promise.reject(res))) // "ok" will check for status code from 200 to 299
        .then(r => r.json())
        .then(res => {
            if (!silent) {
                globalNotification.success(i18n(`The goal has been updated successfully`))
            }

            return res
        })
        .catch(err => {
            if (!err?.status) {
                // log error message to debug
                console.error('An internal error occurred while updating the goal:\n', err)
                globalNotification.error('An internal error occurred while updating the goal')

                return
            }
            err?.json().then(body =>
                globalNotification.error(
                    `${i18n(`For some reason the goal could not be updated`)}: ${body?.message ?? err?.statusText} - (${
                        err?.status
                    })`
                )
            )
        })
}

export function deleteKpiDataElement(
    elId: string,
    configType: ParamTypes,
    assetId = 'GLOBAL'
): Promise<ConfigResponseTypes | void> {
    return fetch(`/services/plugin-kpis-drilling/config/${configType}/${assetId}/${elId}`, {
        method: 'DELETE'
    })
        .then(res => (res?.ok ? res : Promise.reject(res))) // "ok" will check for status code from 200 to 299
        .then(r => r.json())
        .then(res => {
            globalNotification.success(i18n(`The goal was successfully deleted`))

            return res
        })
        .catch(err => {
            if (!err?.status) {
                // log error message to debug
                console.error('An internal error occurred while deleting the goal:\n', err)
                globalNotification.error('An internal error occurred while deleting the goal')

                return
            }
            err?.json().then(body =>
                globalNotification.error(
                    `${i18n(`For some reason the goal could not be deleted`)}: ${body?.message ?? err?.statusText} - (${
                        err?.status
                    })`
                )
            )
        })
}

/**
 * Fetch dictionary data
 */
export function fetchDictionaryData(assetId = 'GLOBAL'): Promise<DictionaryResponseTypes> {
    return fetch(`/services/plugin-kpis-drilling/config/dictionary/${assetId}`)
        .then(async r => {
            const body = await r?.json().catch(() => undefined)

            if (!r?.ok || !body) {
                throw { status: r?.status, body }
            }

            return body
        })
        .catch(err => {
            console.error('Error fetching dictionary:', err)

            if (err?.status) {
                globalNotification.error(<span>{`${i18n(`Failed to load dictionary data`)}: ${err?.status}`}</span>, 10)
            } else {
                globalNotification.error(i18n('Failed to load dictionary data'))
            }

            return Promise.reject(err)
        })
}

/**
 * Update dictionary entry
 */
export function updateDictionaryEntry(
    globalOrAssetId: string,
    entryId: string,
    entry: DictionaryEntry
): Promise<DictionaryResponseTypes | void> {
    return fetch(`/services/plugin-kpis-drilling/config/dictionary/${globalOrAssetId}/${entryId}/displayCode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
    })
        .then(res => (res?.ok ? res : Promise.reject(res)))
        .then(r => r.json())
        .then(res => {
            globalNotification.success(i18n(`Dictionary entry updated successfully`))

            return res
        })
        .catch(err => {
            if (!err?.status) {
                console.error('An internal error occurred while updating the dictionary entry:\n', err)
                globalNotification.error('An internal error occurred while updating the dictionary entry')

                return
            }
            err?.json().then(body =>
                globalNotification.error(
                    `${i18n(`Failed to update dictionary entry`)}: ${body?.message ?? err?.statusText} - (${
                        err?.status
                    })`
                )
            )
        })
}

/**
 * Restore the default display code (= system code) of a dictionary entry.
 * PUT /services/plugin-kpis-drilling/config/dictionary/{entryId}/displayCode/restore
 *
 * Server-authoritative: no body is sent — the backend sets displayCode = systemCode.
 * Goals without a Code Management override reflect the restored default on the next
 * fetch; CM-overridden goals keep their custom code (higher precedence).
 */
export function restoreDictionaryDisplayCode(entryId: string): Promise<DictionaryResponseTypes | void> {
    return fetch(`/services/plugin-kpis-drilling/config/dictionary/${entryId}/displayCode/restore`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => (res?.ok ? res : Promise.reject(res)))
        .then(r => r.json())
        .catch(err => {
            if (!err?.status) {
                console.error('An internal error occurred while restoring the display code:\n', err)
                globalNotification.error('An internal error occurred while restoring the display code')

                return
            }
            err?.json().then(body =>
                globalNotification.error(
                    `${i18n(`Failed to restore display code`)}: ${body?.message ?? err?.statusText} - (${err?.status})`
                )
            )
        })
}
/**
 * Fetch code management data
 */
export function fetchCodeManagementData(): Promise<CodeManagementResponseTypes> {
    return fetch('/services/plugin-kpis-drilling/config/codeManagement/GLOBAL')
        .then(async r => {
            const body = await r?.json().catch(() => undefined)

            if (!r?.ok || !body) {
                const error = new Error('Failed to fetch code management data', {
                    cause: { status: r?.status, body }
                })
                throw error
            }

            return body
        })
        .catch(err => {
            console.error('Error fetching code management data:', err)

            if (err?.status) {
                globalNotification.error(
                    <span>{`${i18n(`Failed to load code management data`)}: ${err?.status}`}</span>,
                    10
                )
            } else {
                globalNotification.error(i18n('Failed to load code management data'))
            }

            return Promise.reject(err)
        })
}

/**
 * Update displayCode of a Code Management entry
 * PUT /services/plugin-kpis-drilling/config/codeManagement/{entryId}/displayCode
 */
export function updateCodeManagementDisplayCode(
    entryId: string,
    updateData: { systemCode: string; displayCode: string } | CMEntry
): Promise<CodeManagementResponseTypes | void> {
    return fetch(`/services/plugin-kpis-drilling/config/codeManagement/${entryId}/displayCode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
    })
        .then(res => (res?.ok ? res : Promise.reject(res)))
        .then(r => r.json())
        .then(res => {
            globalNotification.success(i18n(`Display code updated successfully`))

            return res
        })
        .catch(err => {
            if (!err?.status) {
                console.error('An internal error occurred while updating the display code:\n', err)
                globalNotification.error('An internal error occurred while updating the display code')

                return
            }
            err?.json().then(body =>
                globalNotification.error(
                    `${i18n(`Failed to update display code`)}: ${body?.message ?? err?.statusText} - (${err?.status})`
                )
            )
        })
}

const LIVERIG_ASSETS_URL = '/services/plugin-liverig/assets'

type LiveRigAsset = { id?: string | number; name?: string }

function fetchAssetNames(assetType: 'rig' | 'well'): Promise<LiveRigAsset[]> {
    return fetch(`${LIVERIG_ASSETS_URL}/${assetType}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => (res?.ok ? res.json() : Promise.reject(res)))
        .catch(err => {
            // A tela de Gerenciamento de Códigos continua utilizável sem os nomes — as linhas
            // apenas seguem mostrando o id do ativo. Por isso o erro é registrado, não propagado.
            console.error(`Could not list ${assetType} assets to resolve their names:\n`, err)

            return []
        })
}

/**
 * Chave de {@link fetchAssetNamesById}: sonda e poço têm sequências de id independentes no
 * `plugin-liverig`, então o mesmo id pode nomear uma sonda e um poço diferentes (OGP21-5240).
 * Uma chave só por id faz um nome sobrescrever o outro quando eles colidem.
 */
export function assetNameKey(assetType: 'RIG' | 'WELL', assetId: string): string {
    return `${assetType}:${assetId}`
}

/**
 * Mapa `"RIG:id" | "WELL:id" -> nome do ativo`, unindo sondas e poços.
 *
 * Usado pelo Gerenciamento de Códigos para exibir o nome da sonda/poço na coluna Ativo, no
 * lugar do id do settings node, que não diz nada ao operador (OGP21-5223, REQ-F-009). A chave
 * inclui o tipo do ativo porque sonda e poço têm sequências de id independentes e podem colidir
 * (OGP21-5240) — uma chave só por id faria o nome de um sobrescrever o do outro.
 * Nunca rejeita: quando a listagem falha, devolve o que conseguiu — possivelmente vazio — e
 * quem chama mantém o id como rótulo.
 */
export function fetchAssetNamesById(): Promise<Record<string, string>> {
    return Promise.all([fetchAssetNames('rig'), fetchAssetNames('well')]).then(([rigs, wells]) => {
        const index = (assets: LiveRigAsset[], assetType: 'RIG' | 'WELL', namesById: Record<string, string>) =>
            assets.reduce((acc, asset) => {
                if (asset?.id != null && asset?.name) {
                    acc[assetNameKey(assetType, String(asset.id))] = asset.name
                }

                return acc
            }, namesById)

        return index(wells, 'WELL', index(rigs, 'RIG', {}))
    })
}
